// @ts-nocheck
/**
 * Compact 4-tile stat strip above the users list. Derived from the same
 * `userlist` payload the DataTable already renders — no extra API call.
 *
 *   Total Users   — count of everyone visible
 *   Active        — count with status == 1 (or 'Active')
 *   Expired       — count with expiry_date in the past
 *   Roles         — distinct role buckets present
 *
 * Icon well + gradient + big value + tiny hint, matching the dashboard KPI
 * tile treatment so the same tone language ripples through the app.
 */
import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Users, ShieldCheck, CalendarX, Layers } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { CountUp } from '@/features/dashboard/components/CountUp';

interface Props {
  users: any[];
}

type Tone = 'info' | 'positive' | 'danger' | 'accent';

const TONE_STYLES: Record<Tone, { bg: string; text: string }> = {
  info: {
    bg: 'bg-gradient-to-br from-sky-100 to-sky-200 dark:from-sky-900/60 dark:to-sky-800/50',
    text: 'text-sky-700 dark:text-sky-300',
  },
  positive: {
    bg: 'bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/60 dark:to-emerald-800/50',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  danger: {
    bg: 'bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/60 dark:to-rose-800/50',
    text: 'text-rose-700 dark:text-rose-300',
  },
  accent: {
    bg: 'bg-gradient-to-br from-violet-100 to-violet-200 dark:from-violet-900/60 dark:to-violet-800/50',
    text: 'text-violet-700 dark:text-violet-300',
  },
};

function isExpired(u: any): boolean {
  const d = u?.expiry_date;
  if (!d) return false;
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return false;
  t.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return t.getTime() < today.getTime();
}

function isStatusActive(u: any): boolean {
  const s = u?.status;
  return s === 1 || s === '1' || s === 'Active';
}

// "Active" for the strip means BOTH: status flag active AND expiry in the
// future. Without this an expired user with a stale status='Active' flag
// would land in both the Active and Expired buckets and the totals would
// stop adding up (see also `isBlocked`).
function isActive(u: any): boolean {
  return isStatusActive(u) && !isExpired(u);
}

interface TileProps {
  icon: LucideIcon;
  label: string;
  value: number;
  hint?: string;
  tone: Tone;
}

function StatTile({ icon: Icon, label, value, hint, tone }: TileProps) {
  const t = TONE_STYLES[tone];
  return (
    <div className="flex items-center gap-2 rounded-lg px-3 py-1">
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
          t.bg,
          t.text,
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
      </span>
      <div className="min-w-0 flex-1 leading-none">
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-bold tabular-nums leading-none tracking-tight text-foreground">
            <CountUp value={value} />
          </span>
          <span className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          {hint && (
            <span className="truncate text-[10px] text-muted-foreground/80">{hint}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function UserStatStrip({ users }: Props) {
  const stats = useMemo(() => {
    const total = users.length;
    let active = 0;
    let expired = 0;
    const roles = new Set<string>();
    for (const u of users) {
      if (isActive(u)) active += 1;
      if (isExpired(u)) expired += 1;
      if (u?.role) roles.add(u.role);
    }
    return { total, active, expired, roleCount: roles.size };
  }, [users]);

  const activeRate =
    stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : null;

  // Single horizontal strip — one raised card, four inline stats separated
  // by a thin divider. Trades vertical footprint for wider colour reads.
  // Grid keeps them equal-width; a light divider between columns replaces
  // the four separate raised tiles the earlier design used.
  return (
    <div className="shadow-neu-raised grid shrink-0 grid-cols-2 divide-border rounded-xl bg-card md:grid-cols-4 md:divide-x">
      <StatTile
        icon={Users}
        label="Total"
        value={stats.total}
        tone="info"
      />
      <StatTile
        icon={ShieldCheck}
        label="Active"
        value={stats.active}
        tone="positive"
        hint={activeRate != null ? `${activeRate}%` : undefined}
      />
      <StatTile
        icon={CalendarX}
        label="Expired"
        value={stats.expired}
        tone="danger"
      />
      <StatTile
        icon={Layers}
        label="Roles"
        value={stats.roleCount}
        tone="accent"
      />
    </div>
  );
}
